import {
  Sidebar,
  SidebarGroup,
  SidebarItem,
  SidebarSpace,
  useSidebarOpenState,
} from '@backstage/core-components';
import {
  NavContentBlueprint,
  NavContentComponentProps,
  NavContentNavItem,
} from '@backstage/plugin-app-react';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';
import { SidebarLogo } from './SidebarLogo';
import CloudIcon from '@material-ui/icons/Cloud';
import HomeIcon from '@material-ui/icons/Home';
import ListAltIcon from '@material-ui/icons/ListAlt';
import SupervisorAccountIcon from '@material-ui/icons/SupervisorAccount';
import { useRbac } from '../rbac';
import { lightTokens } from '../theme/rafayTheme';
import { APP_VERSION } from '../appVersion';

const useSectionLabelStyles = makeStyles({
  root: {
    padding: '6px 16px',
    marginTop: 4,
    fontSize: '0.625rem',
    fontWeight: 600,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    color: lightTokens.sidebarMutedText,
  },
});

// Backstage's own SidebarGroup only renders its `label` prop on mobile
// (bottom nav) — on desktop it's a no-op passthrough of `children` (see
// @backstage/core-components' SidebarGroup.tsx). The reference design's
// always-visible uppercase section headings need their own element instead.
const SidebarSectionLabel = ({ children }: { children: string }) => {
  const classes = useSectionLabelStyles();
  return (
    <Typography component="div" className={classes.root}>
      {children}
    </Typography>
  );
};

const useVersionStyles = makeStyles({
  root: {
    padding: '8px 16px',
    fontSize: '0.625rem',
    letterSpacing: '0.02em',
    whiteSpace: 'nowrap',
    color: lightTokens.sidebarMutedText,
    opacity: 0.8,
  },
});

// Subtle build marker at the foot of the sidebar. Collapses to "v1.4" when
// the sidebar is narrow; the full version is always in the tooltip.
const SidebarVersion = () => {
  const classes = useVersionStyles();
  const { isOpen } = useSidebarOpenState();
  return (
    <Typography
      component="div"
      className={classes.root}
      title={`Control Tower ${APP_VERSION}`}
    >
      {isOpen
        ? `Control Tower v${APP_VERSION}`
        : `v${APP_VERSION.split('.').slice(0, 2).join('.')}`}
    </Typography>
  );
};

const SidebarNav = ({ navItems }: NavContentComponentProps) => {
  const { permissions, loading } = useRbac();
  const can = (permission: string) =>
    !loading && permissions.includes(permission as any);

  const nav = navItems.withComponent((item: NavContentNavItem) => (
    <SidebarItem icon={() => item.icon} to={item.href} text={item.title} />
  ));

  nav.take('page:search');

  const myVmsItem = nav.take('page:infrastructure/my-vms');
  // "Keep VM Running" (the wizard) and "Requests" (the combined VM
  // provisioning + runtime extension request tracker) replace what used to
  // be three separate items here: My Requests, All Requests, and Keep VM
  // Running Requests — see plugin.tsx / RequestsPage.tsx for why those
  // routes still exist but are no longer linked from here.
  const keepVmRunningItem = nav.take('page:runtime-extension/keep-vm-running');
  const requestsItem = nav.take('page:infrastructure/requests');
  // Approvals now has Pending Approvals + Approval History as tabs inside
  // one page — no separate Approval History nav item anymore.
  const approvalsItem = nav.take('page:infrastructure/approvals');
  const userManagementItem = nav.take('page:infrastructure/user-management');
  const vmGovernanceItem = nav.take('page:infrastructure/vm-governance');

  // Admins + super admins can review; only super admins see User Management.
  const canReview = can('request.approve.assigned');
  const canManageUsers = can('user.manage');
  const canGovern = can('vm.governance');

  return (
    <Sidebar>
      <SidebarLogo />
      <SidebarItem icon={HomeIcon} to="/" text="Home" />
      <SidebarSectionLabel>Compute</SidebarSectionLabel>
      <SidebarGroup label="Compute" icon={<CloudIcon />}>
        {myVmsItem}
      </SidebarGroup>
      <SidebarSectionLabel>Operations</SidebarSectionLabel>
      <SidebarGroup label="Operations" icon={<ListAltIcon />}>
        {keepVmRunningItem}
        {requestsItem}
        {canReview && approvalsItem}
      </SidebarGroup>
      {(canManageUsers || canGovern) && (
        <>
          <SidebarSectionLabel>Administration</SidebarSectionLabel>
          <SidebarGroup label="Administration" icon={<SupervisorAccountIcon />}>
            {canManageUsers && userManagementItem}
            {canGovern && vmGovernanceItem}
          </SidebarGroup>
        </>
      )}
      <SidebarSpace />
      <SidebarVersion />
    </Sidebar>
  );
};

export const SidebarContent = NavContentBlueprint.make({
  params: {
    component: SidebarNav,
  },
});
